import { useMemo } from "react";
import { Skeleton } from "../../../../../shared/components/ui/skeleton";
import Icons from "../../../../../shared/constants/icon.constant";
import {
  buildWalletEntries,
  computeWalletSummary,
  useUserBalances,
} from "../../../services";
import CryptoCoinCard from "../components/CryptoCoinCard";
import FiatAccountCard from "../components/FiatAccountCard";
import WalletBallance from "../components/WalletBalance";

const tokenIconMap: Record<string, string> = {
  BTC: "/assets/icons/bitcoin-icon.png",
  ETH: "/assets/icons/eth-icon.png",
  DOGE: "/assets/icons/doge-icon.png",
  USDT: "/assets/icons/usdt-icon.png",
};

const getTokenIcon = (symbol: string) => tokenIconMap[symbol] ?? "";

export default function Wallet() {
  const {
    data: balancesData,
    isLoading: isLoadingBalances,
    error: balancesError,
  } = useUserBalances();

  const primaryBalance = balancesData?.balances?.[0];
  const baseCurrency =
    primaryBalance?.currency ||
    balancesData?.fiatBalances?.[0]?.name ||
    balancesData?.cryptoBalances?.[0]?.name ||
    "USD";

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: baseCurrency,
        maximumFractionDigits: 2,
      });
    } catch {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });
    }
  }, [baseCurrency]);

  const formatCurrencyValue = (value: number) =>
    currencyFormatter.format(Number.isFinite(value) ? value : 0);

  const summary = useMemo(
    () => computeWalletSummary(balancesData),
    [balancesData]
  );

  const { cryptoEntries } = useMemo(
    () => buildWalletEntries(balancesData),
    [balancesData]
  );

  const showAggregatedView = summary.hasDetailedBalances;
  const walletAmountDisplay = showAggregatedView
    ? formatCurrencyValue(summary.totalWalletValue)
    : primaryBalance?.balance;
  const walletCurrencyLabel = primaryBalance?.currency ?? baseCurrency;

  const cryptoWalletValue = showAggregatedView
    ? formatCurrencyValue(summary.totalCryptoValue)
    : "--";
  const fiatWalletValue = showAggregatedView
    ? formatCurrencyValue(summary.totalFiatValue)
    : "--";

  const errorMessage = balancesError?.message;

  const cryptoAssets = cryptoEntries;

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex gap-4 items-start">
        <div className="flex-1 flex flex-col gap-4">
          <div className="w-full h-60">
            <WalletBallance
              amountDisplay={
                isLoadingBalances || balancesError
                  ? undefined
                  : walletAmountDisplay
              }
              currencyLabel={
                isLoadingBalances || balancesError
                  ? undefined
                  : walletCurrencyLabel
              }
              isLoading={isLoadingBalances}
              errorMessage={errorMessage}
            />
          </div>
          <div className="w-full flex gap-4 h-32">
            <div className="flex-1">
              <FiatAccountCard
                title="Crypto Wallet"
                icon={Icons.Coin({ height: 25, width: 25 })}
                value={
                  isLoadingBalances
                    ? "..."
                    : balancesError
                      ? "--"
                      : cryptoWalletValue
                }
              />
            </div>
            <div className="flex-1">
              <FiatAccountCard
                title="Fiat Account"
                icon={Icons.Bank({ height: 25, width: 25 })}
                value={
                  isLoadingBalances
                    ? "..."
                    : balancesError
                      ? "--"
                      : fiatWalletValue
                }
              />
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <div className="w-full text-lg mt-2">My Assets</div>
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoadingBalances ? (
              Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={`asset-skeleton-${index}`}
                  className="h-32 w-full rounded-xl bg-white/10"
                />
              ))
            ) : balancesError ? (
              <p className="col-span-full text-sm text-red-400">
                {errorMessage ?? "Unable to load assets."}
              </p>
            ) : cryptoAssets.length ? (
              cryptoAssets.map((entry) => {
                const amountLabel = entry.amountDisplay?.trim().length
                  ? entry.amountDisplay
                  : entry.amount.toString();

                return (
                  <CryptoCoinCard
                    key={entry.id}
                    logo={getTokenIcon(entry.currency)}
                    code={entry.currency || "—"}
                    amount={`${amountLabel} ${entry.currency}`.trim()}
                    equivalent={formatCurrencyValue(entry.usdValue)}
                  />
                );
              })
            ) : (
              <p className="col-span-full text-sm text-white/60">
                No crypto assets yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
